/**
 * The payment configuration vocabularies — every one of them declared here once
 * and derived everywhere else (AGENTS.md Rule #7c).
 *
 * The epic that made this file the single home (#773) names three levels, and
 * all three live below alongside the two row-level vocabularies the catalogue
 * needs. They are kept in one file deliberately: the failure this replaces was
 * "payment method" meaning six different things in six different places, and a
 * reader can only check that they are still distinct if they are side by side.
 *
 * | Level | Declaration | What it names |
 * |---|---|---|
 * | 1 | {@link PAYMENT_PROVIDER_TYPES} | the kind of provider — `ONLINE_GATEWAY`, `EDC`, `CASHLESS`, `CASH` |
 * | 2 | *(no vocabulary)* | a provider is a database row keyed by `slug`, not a closed list — see below |
 * | 3 | {@link CHECKOUT_METHODS} | the coarse method a customer picks before paying |
 * | — | {@link PAYMENT_METHOD_CODES} | the granular code a gateway webhook resolves afterwards. **Reporting only**, deliberately not level 3 |
 * | — | {@link SALES_CHANNELS} | where a sale was taken; scopes both catalogue rows and routes |
 * | — | {@link PAYMENT_FEE_TYPES} | how a catalogue row expresses its fee |
 *
 * **Level 2 has no `as const` on purpose.** Providers are rows in
 * `payment_providers`, added and removed by an operator without a deploy, so a
 * closed union would be a second source of truth that the database contradicts
 * the first time someone seeds a row. The one closed provider list that does
 * exist — `PaymentGatewaySchema` in `@autopia/api-contracts` — is a mirror of the
 * Prisma `PaymentGatewayProvider` column and answers a narrower question: which
 * providers have a charge adapter and an enum member. Adding one of those is a
 * migration, which is why this epic promises enable/disable/reorder/reroute
 * without a deploy and does not promise provider onboarding without one.
 */

/**
 * Canonical payment-method codes — SINGLE SOURCE OF TRUTH.
 *
 * These are the granular, gateway-resolved codes that `PaymentMethodResolver`
 * writes into `Payment.paymentMethod`, that the admin payments filter queries
 * by, and that the API contract validates. Anything that produces, stores,
 * filters, or labels a payment method MUST derive from this list — do not
 * redefine these strings anywhere else (see CLAUDE.md rule #1).
 *
 * Why granular (Option A): the resolver already knows the specific bank/channel
 * from the gateway webhook (BCA vs Mandiri, Akulaku vs Kredivo). Finance
 * reconciliation and EDC settlement work per-bank, so we keep that detail
 * rather than collapsing it into a lossy `BANK_TRANSFER`.
 *
 * This is the code resolved **after** the fact. For the coarse code the customer
 * picks **before** paying, see {@link CHECKOUT_METHODS} at the foot of this file.
 * The two are deliberately separate and nothing maps between them at charge
 * time — see ADR-036 §2b.
 */

export const PAYMENT_METHOD_CODES = [
  // Cards
  'CREDIT_CARD',
  // Virtual accounts (per bank)
  'BCA_VA',
  'MANDIRI_VA',
  'BNI_VA',
  'BRI_VA',
  'CIMB_VA',
  'PERMATA_VA',
  'OTHER_VA', // catch-all for an unrecognised bank VA (forward-compatible)
  // Bank bill payment
  'MANDIRI_BILL',
  // E-wallets
  'GOPAY',
  'OVO',
  'DANA',
  'SHOPEEPAY',
  'LINKAJA',
  // QR
  'QRIS',
  // Pay-later
  'AKULAKU',
  'KREDIVO',
  // Retail / over-the-counter
  'CONVENIENCE_STORE',
  // In-person tenders taken at a booth, kiosk or EDC terminal (#789).
  //
  // These four are NOT resolver output — no gateway webhook produces them.
  // `kiosk-service` and the booth record them directly, which is exactly why
  // they were missing: this list grew from what the resolver could emit, and an
  // in-person sale never passes through a resolver. The consequence was that
  // booth and kiosk card sales had no canonical code, could not be filtered in
  // the admin payments list, and could not be broken out in finance reports.
  //
  // Per-TENDER, not per-bank. One physical terminal accepts a card sale and a
  // QRIS payment; the BANK is carried by the provider row and the settlement
  // table, and the tender is a genuinely different thing. Six per-bank codes
  // would encode a distinction that already exists elsewhere. Follows the
  // monolith's migration 114.
  'CASH',
  'CASHLESS',
  'EDC_CARD',
  'EDC_QRIS',
  // Fallback for any code the resolver cannot map (forward-compatible)
  'UNKNOWN',
] as const;

/** Union of every valid canonical payment-method code. */
export type PaymentMethodCode = (typeof PAYMENT_METHOD_CODES)[number];

const PAYMENT_METHOD_CODE_SET: ReadonlySet<string> = new Set(PAYMENT_METHOD_CODES);

/**
 * Type guard: `true` when `value` is a member of {@link PAYMENT_METHOD_CODES}.
 * Used by the resolver guard test to ensure every resolver output is canonical.
 */
export function isPaymentMethodCode(value: unknown): value is PaymentMethodCode {
  return typeof value === 'string' && PAYMENT_METHOD_CODE_SET.has(value);
}

/**
 * The group headers the admin filter dropdown renders over
 * {@link PAYMENT_METHOD_CODES} — SINGLE SOURCE OF TRUTH.
 *
 * Not to be confused with {@link CHECKOUT_GROUPS}, which groups the *coarse*
 * checkout vocabulary in the customer-facing picker. The two overlap on five ids
 * and are still different vocabularies, because they group different lists: this
 * one has `payLater` and `retail`, which exist only as resolved codes and can
 * never be picked, and its `inPerson` holds four granular tender codes where the
 * picker's holds two coarse ones. Documented at both declarations per AGENTS.md
 * Rule #7a.
 */
export const PAYMENT_METHOD_GROUP_IDS = [
  'card',
  'bankTransfer',
  'ewallet',
  'qris',
  'payLater',
  'retail',
  'inPerson',
] as const;

/** Union of every admin-filter group header id. */
export type PaymentMethodGroupId = (typeof PAYMENT_METHOD_GROUP_IDS)[number];

/**
 * Visual grouping for the admin filter dropdown. Each group has a stable `id`
 * (the UI maps it to a localised header) and the codes shown beneath it.
 * Order here drives the order rendered in the UI.
 */
export interface PaymentMethodGroup {
  /** Stable identifier; the UI resolves this to a localised group header. */
  id: PaymentMethodGroupId;
  codes: PaymentMethodCode[];
}

/**
 * Filterable methods grouped for the admin dropdown. `UNKNOWN` is intentionally
 * excluded — it is a resolver fallback that the persistence guard rejects, so it
 * is never stored and filtering by it would always be empty.
 */
export const PAYMENT_METHOD_GROUPS: readonly PaymentMethodGroup[] = [
  { id: 'card', codes: ['CREDIT_CARD'] },
  {
    id: 'bankTransfer',
    codes: [
      'BCA_VA',
      'MANDIRI_VA',
      'BNI_VA',
      'BRI_VA',
      'CIMB_VA',
      'PERMATA_VA',
      'OTHER_VA',
      'MANDIRI_BILL',
    ],
  },
  { id: 'ewallet', codes: ['GOPAY', 'OVO', 'DANA', 'SHOPEEPAY', 'LINKAJA'] },
  { id: 'qris', codes: ['QRIS'] },
  { id: 'payLater', codes: ['AKULAKU', 'KREDIVO'] },
  { id: 'retail', codes: ['CONVENIENCE_STORE'] },
  // #789. Grouped together rather than split into `cash` and `edc` because the
  // group is a dropdown header, and four options under one header is easier to
  // scan than two headers of two. Finance's per-bank EDC breakdown does not come
  // from here — the bank lives on the provider row and the settlement table.
  { id: 'inPerson', codes: ['CASH', 'CASHLESS', 'EDC_CARD', 'EDC_QRIS'] },
] as const;

// ============================================================================
// EDC bank codes
// ============================================================================

/**
 * The banks whose EDC terminals the park accepts — SINGLE SOURCE OF TRUTH.
 *
 * ## Why this exists: `CIMB` vs `CIMB_NIAGA` (#786)
 *
 * This list was hardcoded **seven** times across four workspaces, and six of the
 * seven agreed. The seventh —
 * `payment-service/src/reconciliation/gateways/edc-reconciliation.adapter.ts` —
 * said `CIMB_NIAGA`, so a CIMB settlement file reconciled against a bank code no
 * transaction was ever recorded under. The other five banks agreeing is exactly
 * what made it easy to miss.
 *
 * **`CIMB` is canonical**, not `CIMB_NIAGA`. #786 sketched the constant the other
 * way round; the evidence says otherwise and the issue asked for the check to be
 * made rather than assumed:
 *
 * - The **producer** says `CIMB`. `CimbEdcAdapter.bankCode` is `'CIMB'` and
 *   `kiosk-service` registers the adapter under that key — it is the value a real
 *   transaction is recorded with.
 * - The **settlement parser** branches on `case 'CIMB'`.
 * - `payment_service.edc_settlement_files.bank_code` is `VarChar(10)`, and its
 *   schema comment already documents the set as `... | CIMB | ...`. `CIMB_NIAGA`
 *   is exactly 10 characters — it fits with no headroom at all.
 * - Six declarations would have had to change to adopt `CIMB_NIAGA`, against one
 *   to adopt `CIMB`.
 *
 * **No data migration was required, and that was verified rather than assumed.**
 * At the time of the change `kiosk_transactions` held zero rows in
 * `autopia_kiosk` and three in `autopia_kiosk_test`, all with `edc_bank IS NULL`;
 * `edc_settlement_files` and `edc_settlement_rows` were empty in both
 * `autopia_payment` and `autopia_payment_test`. No EDC transaction has ever been
 * recorded under either spelling.
 *
 * ## Not a provider slug
 *
 * The provider rows for these banks are `edc-bca`, `edc-permata` and so on
 * (#787). This list is the BANK, which is one field of such a row; the slug is
 * the URL key. They are related by convention, not by equality — do not derive
 * one from the other by string manipulation.
 */
export const EDC_BANK_CODES = ['BCA', 'MANDIRI', 'BNI', 'BRI', 'CIMB', 'PERMATA'] as const;

/** Union of every EDC bank the park accepts terminals from. */
export type EdcBankCode = (typeof EDC_BANK_CODES)[number];

const EDC_BANK_CODE_SET: ReadonlySet<string> = new Set(EDC_BANK_CODES);

/**
 * Type guard: `true` when `value` is a member of {@link EDC_BANK_CODES}.
 *
 * Every entry point that accepts a bank code from outside — a settlement upload,
 * an admin query parameter, a kiosk request — must use this rather than a cast.
 * `strictNullChecks` is off repo-wide and services build with swc, so the
 * narrowed type is documentation; this is the enforcement.
 */
export function isEdcBankCode(value: unknown): value is EdcBankCode {
  return typeof value === 'string' && EDC_BANK_CODE_SET.has(value);
}

// ============================================================================
// Provider types — level 1
// ============================================================================

/**
 * What kind of thing takes the money — SINGLE SOURCE OF TRUTH.
 *
 * Level 1 of the three-level vocabulary. It groups providers on the admin
 * configuration hub and decides which surface configures them: an
 * `ONLINE_GATEWAY` has a charge path and a credential held by its adapter, an
 * `EDC` is a physical terminal reached out-of-band by `kiosk-service`, and
 * `CASHLESS` and `CASH` are tenders with no implementation in this repository
 * yet (`payment-configuration.md` §9).
 *
 * Named `PaymentProviderType`, not `ProviderType`, to pair with the
 * `PaymentProvider` row it describes; and not `PaymentType`, which already means
 * two other things here — `payment_type` is Midtrans's wire field for the
 * granular method (`payment-method-resolver.ts:18`) and `paymentType` is our own
 * integration kind, `snap | qr_code | virtual_account | redirect`
 * (`gateway.interface.ts`). `gateway-response-reader.ts:145` already carries a
 * comment warning readers not to confuse those two; a third meaning would have
 * made that comment useless.
 *
 * Mirrored by the Prisma `PaymentProviderType` enum in payment-service, which is
 * the one duplication database-per-service forces — a package cannot import a
 * service's generated client. That mirror carries a parity test
 * (`payment-configuration-vocabulary.spec.ts`); `api-contracts` derives with
 * `z.enum(PAYMENT_PROVIDER_TYPES)` and holds no list of its own.
 */
export const PAYMENT_PROVIDER_TYPES = ['ONLINE_GATEWAY', 'EDC', 'CASHLESS', 'CASH'] as const;

/** Union of every kind of payment provider the park can configure. */
export type PaymentProviderType = (typeof PAYMENT_PROVIDER_TYPES)[number];

// ============================================================================
// Sales channels
// ============================================================================

/**
 * Where a sale was taken — SINGLE SOURCE OF TRUTH.
 *
 * Scopes two independent axes: which channels a catalogue row is offered on
 * (`PaymentMethod.salesChannels`) and which channel a route applies to
 * (`PaymentMethodRoute.salesChannel`).
 *
 * ## `ALL` is a sentinel, not a channel
 *
 * It means "every channel", and it exists instead of a nullable column because
 * PostgreSQL treats `NULL`s as **distinct** in a unique constraint. A nullable
 * `salesChannel` would let `@@unique([paymentMethodId, providerId, salesChannel])`
 * admit unlimited duplicate `(method, provider, NULL)` rows — protecting nothing,
 * precisely in the set every seeded route lands in. Prisma 5.9, this service's
 * version, has no declarative `NULLS NOT DISTINCT`. Full reasoning in
 * `payment-configuration.md` §4.5.
 *
 * Consumers must therefore treat `ALL` as a wildcard on read, never as a value a
 * sale can carry. A channel-specific route list **overrides** the `ALL` list
 * rather than merging with it (§4.3).
 *
 * ## Not to be confused with two other channel vocabularies
 *
 * Both are legitimate and neither is this one (AGENTS.md Rule #7a — where two
 * layers hold the same concept with different values, confirm it deliberately
 * and document it at both declarations):
 *
 * - `ChannelSchema` in `@autopia/event-schemas` (`common.ts`) — eight lowercase
 *   values including `online_web`/`online_mobile`, `walkup`, `corporate` and
 *   `internal`. It records where an *order* originated, on the Kafka wire, at a
 *   finer grain than payment routing needs. It has live producers; do not
 *   redirect it here.
 * - `PaymentChannel` in `apps/web/src/components/payment/virtual-account/bank-data.ts`
 *   — `'atm' | 'mobile' | 'internet'`, the *bank* channel a customer uses to pay
 *   a virtual account. Unrelated to both; out of scope.
 *
 * A third, `PaymentChannel` in this package's `payment.types.ts`, is what this
 * list replaces. It was `'online' | 'kiosk' | 'ticket_booth' | 'reseller'` and
 * was imported by nothing — see the note left in its place.
 *
 * ## Two lists, one declaration
 *
 * The channels a real sale can be taken on are declared first, and
 * {@link SALES_CHANNELS} prepends the sentinel to them. Declaring both lists by
 * hand would be the same duplication this file exists to remove, one level down
 * — and the copy that drifted would be the one deciding whether a route matches.
 */
export const CONCRETE_SALES_CHANNELS = ['WEB', 'KIOSK', 'BOOTH', 'RESELLER', 'GROUP'] as const;

/** Union of every sales channel a real sale can be taken on. Excludes `ALL`. */
export type ConcreteSalesChannel = (typeof CONCRETE_SALES_CHANNELS)[number];

/**
 * Every sales channel a configuration row can be scoped to, including the `ALL`
 * wildcard sentinel. Derived from {@link CONCRETE_SALES_CHANNELS} — see above.
 */
export const SALES_CHANNELS = ['ALL', ...CONCRETE_SALES_CHANNELS] as const;

/** Union of every sales channel, including the `ALL` wildcard sentinel. */
export type SalesChannel = (typeof SALES_CHANNELS)[number];

/**
 * The sentinel member of {@link SALES_CHANNELS}. Exported so a consumer can
 * express "the wildcard" without re-typing the literal, and so a reader grepping
 * for it finds the explanation above.
 */
export const SALES_CHANNEL_ALL = 'ALL' satisfies SalesChannel;

// ============================================================================
// Checkout methods — the coarse vocabulary
// ============================================================================

/**
 * The coarse method a customer picks at CHECKOUT — SINGLE SOURCE OF TRUTH.
 *
 * At selection time the specific bank or channel is not yet known, so this
 * stays generic. It is the input to gateway routing; it is never the value
 * stored in `Payment.paymentMethod`, which is the granular
 * {@link PAYMENT_METHOD_CODES} the webhook resolves afterwards.
 *
 * Every other layer DERIVES from this list and must never re-list its values:
 * `api-contracts` as `z.enum(CHECKOUT_METHODS)`, payment-service by importing
 * {@link CheckoutMethod}. See AGENTS.md Rule #7c.
 *
 * ## How this list was reconciled
 *
 * It replaces two declarations that disagreed: 13 values in payment-service's
 * `gateway.interface.ts` and 11 re-listed literals in `api-contracts`. Nine
 * were shared. The differences were resolved value by value, not unioned:
 *
 * - **The four per-bank VAs are IN** (`bca_va`, `mandiri_va`, `bni_va`,
 *   `bri_va`), despite existing only on the charge path before. They are live
 *   routing keys, not decoration — each routes to Xendit and is translated to a
 *   gateway wire code by `xendit.adapter.ts`'s `VA_BANK_MAP` and
 *   `doku.adapter.ts`'s `DOKU_CHECKOUT_METHOD_MAP`. Collapsing them into
 *   `bank_transfer` would lose the customer's bank choice at charge time.
 *   `PAYMENT_METHOD_CODES` also has `BCA_VA`, but it answers a different
 *   question — what the webhook resolved, not what the customer asked for.
 *
 * - **`cash` and `edc` are IN**, despite existing only at the API boundary
 *   before. Both are real tenders already flowing through the system:
 *   kiosk-service emits them and ticketing-service's addon-order events default
 *   to `cash`. Neither has a gateway route, and that is deliberate — the cash
 *   charge path awaits Ticket Box (#490, Phase 3) and EDC is handled
 *   out-of-band by kiosk-service (Phase 2). `GatewayFactoryService.resolve()`
 *   THROWS for them rather than falling through to a default; routing a cash
 *   sale to Midtrans is the defect this list exists to close.
 *
 * - **`debit_card` is OUT.** It routed nowhere, appeared in no adapter's
 *   `supportedMethods` and in no gateway map, and no producer anywhere in the
 *   monorepo emits it. No gateway distinguishes debit from credit — all three
 *   process cards on one rail — and an in-person debit tap is the `edc` tender.
 *
 * `linkaja` is deliberately absent: it is in neither source declaration, and
 * adding it here would be unioning lists rather than reconciling them. It
 * remains available as a resolved code in {@link PAYMENT_METHOD_CODES}.
 */
export const CHECKOUT_METHODS = [
  // Cards
  'credit_card',
  // Bank transfer — generic, plus the per-bank virtual accounts that route
  'bank_transfer',
  'bca_va',
  'mandiri_va',
  'bni_va',
  'bri_va',
  // E-wallets
  'gopay',
  'ovo',
  'dana',
  'shopeepay',
  // QR
  'qris',
  // Generic Midtrans Snap — the popup offers every method Snap supports
  'snap',
  // Out-of-band tenders: no gateway route, resolve() throws for these
  'edc',
  'cash',
] as const;

/** Union of every coarse method a customer can pick at checkout. */
export type CheckoutMethod = (typeof CHECKOUT_METHODS)[number];

/**
 * The group headers a checkout picker renders over {@link CHECKOUT_METHODS} —
 * SINGLE SOURCE OF TRUTH.
 *
 * Stored on each catalogue row as `PaymentMethod.checkoutGroup` and resolved to
 * a localised header client-side, the same way method labels are (§8 of
 * `payment-configuration.md`). The catalogue carries the id, never the words.
 *
 * `inPerson` covers the tenders taken at a physical point of sale — `edc` and
 * `cash` — which have no gateway route and belong under neither `card` nor
 * `bankTransfer` however they are eventually settled. `other` exists for `snap`,
 * the Midtrans-hosted popup that offers every method Snap supports and so is not
 * one kind of thing.
 *
 * Not to be confused with {@link PAYMENT_METHOD_GROUP_IDS} — see the note there.
 */
export const CHECKOUT_GROUPS = [
  'card',
  'bankTransfer',
  'ewallet',
  'qris',
  'inPerson',
  'other',
] as const;

/** Union of every checkout-picker group header id. */
export type CheckoutGroup = (typeof CHECKOUT_GROUPS)[number];

// ============================================================================
// Payment fees
// ============================================================================

/**
 * How a payment method's fee is expressed — SINGLE SOURCE OF TRUTH.
 *
 * `none` is a real member rather than a null fee, so a method that charges
 * nothing states so explicitly instead of leaving `feeValue` to be interpreted.
 *
 * Declared here because #782 gives every catalogue row a fee type and would
 * otherwise have introduced a third copy: `api-contracts` already inlined this
 * same three-value enum twice, once on `AvailablePaymentMethodSchema` and once
 * on `FeeCalculationSchema`. Both now derive.
 *
 * ## Why uppercase (#781)
 *
 * These values were lowercase when #823 first moved them here, while the Prisma
 * `PaymentFeeType` this list is mirrored by is `NONE | FIXED | PERCENTAGE`
 * (`payment-configuration.md` §4). That is a cross-layer casing mismatch of
 * exactly the kind `PaymentStatus` already carries — where the database says
 * `PAID` and the HTTP contract says `paid`, and a boundary converter has to be
 * remembered at every crossing. §6.1 item 4 required resolving it in one
 * direction before either the catalogue or the public endpoint landed.
 *
 * Uppercase won because it is the direction with no converter at all: this list
 * is the Prisma mirror, and it now matches member for member. It cost nothing to
 * change — `feeType` had no producer and no consumer anywhere in the monorepo
 * when this was done (`GetPaymentMethodsResponseSchema` and `FeeCalculationSchema`
 * are both unserved), so no wire value moved. It also puts this vocabulary in
 * step with {@link PAYMENT_METHOD_CODES}, {@link PAYMENT_PROVIDER_TYPES} and
 * {@link SALES_CHANNELS}, the other three payment vocabularies that are stored
 * rather than selected.
 */
export const PAYMENT_FEE_TYPES = ['NONE', 'FIXED', 'PERCENTAGE'] as const;

/** Union of every way a payment-method fee can be expressed. */
export type PaymentFeeType = (typeof PAYMENT_FEE_TYPES)[number];
